import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { describeDrop, zoneInRow } from './dropFeedback';
import type { DropOutcome } from './dropFeedback';
import type { DropResolution } from './dropTarget';
import { usePlanEditor } from './PlanEditorProvider';
import { subtreeHeight } from './treeOps';

import {
  AUTO_SCROLL_MARGIN_PX,
  AUTO_SCROLL_STEP_PX,
  DEPTH_BASE_PX,
  INDENT_PX,
} from '@/components/planning/treeGeometry';

/**
 * Pointer-based subtree drag (no dnd library — HTML5 DnD fights the
 * virtualizer). Escape cancels. The blue drop indicator renders in fixed
 * coordinates; geometry only — the tree position comes from the pure
 * `resolveDropTarget`.
 *
 * Y picks one of three zones in the row under the cursor (`zoneInRow`, the
 * rule the timeline's drop zones already use):
 *
 * - the top and bottom EDGES are reorder gaps, and there the drag's SIDEWAYS
 *   DISPLACEMENT picks the depth (22 px per level, the row indent) — which is
 *   how a drop can outdent to an ancestor's level, something zones alone
 *   cannot express;
 * - the MIDDLE means *inside this row*, and sideways plays no part;
 *   dropping onto the parent is what people try first.
 */
export type DragIndicator = {
  top: number;
  left: number;
  width: number;
  /** A target moveSubtree would refuse — shown, but never committed. */
  refused: boolean;
};

export const useDragReorder = (scrollElement: HTMLElement | null) => {
  const { t } = useTranslation('pmPlanning');
  const { state, dispatch, flat } = usePlanEditor();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [indicator, setIndicator] = useState<DragIndicator | null>(null);
  /** The row a drop would go INSIDE, so it can say so. */
  const [intoId, setIntoId] = useState<string | null>(null);
  /** What the drop would do, in words — rendered by the drag card. */
  const [outcome, setOutcome] = useState<DropOutcome | null>(null);
  const resolutionRef = useRef<DropResolution | null>(null);

  const planRef = useRef(state.document.plan);
  const flatRef = useRef(flat);
  const foldedRef = useRef(state.foldedIds);
  // The readout's words; a ref like the rest, so a language switch mid-drag is seen.
  const tRef = useRef(t);
  useEffect(() => {
    planRef.current = state.document.plan;
    flatRef.current = flat;
    foldedRef.current = state.foldedIds;
    tRef.current = t;
  });

  /*
    Where the gesture began, so depth can be its DISPLACEMENT rather than the
    pointer's absolute position.

    Absolute X was the bug: the drag handle sits in the row's trailing column,
    at the far right, so `clientX - rect.left` was ~700px on every drag,
    `rawDepth` came out around 33, and the clamp pinned every drop to
    `previous.depth + 1` — the nest branch. A straight vertical drag nested
    every time, and reordering meant hauling the pointer ~750px left into a
    22px window. Displacement makes where the handle happens to live
    irrelevant, which is the property that was missing.
  */
  const originRef = useRef<{ x: number; depth: number; index: number; height: number } | null>(
    null,
  );
  /** The readout card, moved imperatively — a render per pointer move is waste. */
  const cardRef = useRef<HTMLDivElement | null>(null);

  const startDrag = (nodeId: string) => (event: ReactPointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    resolutionRef.current = null;
    const flatNow = flatRef.current;
    const index = flatNow.index.rowIndexById.get(nodeId) ?? 0;
    const node = flatNow.index.nodeById.get(nodeId);
    originRef.current = {
      x: event.clientX,
      depth: flatNow.rows[index]?.depth ?? 0,
      index,
      // Hoisted: never recompute a subtree walk per pointer move.
      height: node ? subtreeHeight(node) : 0,
    };
    setDraggingId(nodeId);
  };

  useEffect(() => {
    if (!draggingId) return undefined;

    /*
      Every early return below MUST clear the pending resolution, not just skip.
      Leaving the last one live meant releasing over the page header or the
      gutter committed a move to wherever the pointer had last been over a row —
      a drop landing somewhere the user was no longer pointing at.
    */
    const clearTarget = () => {
      resolutionRef.current = null;
      setIndicator(null);
      setIntoId(null);
      setOutcome(null);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (cardRef.current) {
        cardRef.current.style.transform = `translate(${String(event.clientX + 14)}px, ${String(
          event.clientY + 16,
        )}px)`;
      }
      const element = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest<HTMLElement>('[data-node-id]');

      if (scrollElement) {
        const rect = scrollElement.getBoundingClientRect();
        if (event.clientY < rect.top + AUTO_SCROLL_MARGIN_PX) {
          scrollElement.scrollTop -= AUTO_SCROLL_STEP_PX;
        } else if (event.clientY > rect.bottom - AUTO_SCROLL_MARGIN_PX) {
          scrollElement.scrollTop += AUTO_SCROLL_STEP_PX;
        }
      }

      if (!element?.dataset.nodeId) {
        clearTarget();
        return;
      }
      const rowIndex = flatRef.current.index.rowIndexById.get(element.dataset.nodeId);
      if (rowIndex === undefined) {
        clearTarget();
        return;
      }

      const origin = originRef.current;
      if (!origin) {
        clearTarget();
        return;
      }

      const rect = element.getBoundingClientRect();
      const zone = zoneInRow(event.clientY - rect.top, rect.height);
      /*
        The edges take the depth from how far the drag has moved SIDEWAYS: no
        horizontal movement keeps the row's own depth (a reorder), one indent
        right nests it, one left outdents it. `into` means this row, and
        sideways plays no part there.
      */
      const depthOverride =
        zone === 'into' ? null : origin.depth + Math.round((event.clientX - origin.x) / INDENT_PX);

      /*
        The same function the timeline reads, so the two surfaces cannot
        disagree about what a drop does — and so the list can say it in words.
        It owns the refusals too (a drop inside the dragged subtree, the BR-23
        ceiling), which this hook used to re-derive.
      */
      const outcome = describeDrop({
        plan: planRef.current,
        flat: flatRef.current,
        dragId: draggingId,
        dragIndex: origin.index,
        dragEndIndex: origin.index + origin.height,
        dragHeight: origin.height,
        targetIndex: rowIndex,
        zone,
        depthOverride,
        foldedIds: foldedRef.current,
        t: tRef.current,
      });
      setOutcome(outcome);

      if (outcome.kind !== 'move') {
        resolutionRef.current = null;
        setIntoId(null);
        // A refusal still draws, in its refused state; a no-op draws nothing.
        if (outcome.kind === 'noop') {
          setIndicator(null);
          return;
        }
        setIndicator({
          top: zone === 'before' ? rect.top : rect.bottom,
          left: rect.left + DEPTH_BASE_PX,
          width: Math.max(80, rect.width - DEPTH_BASE_PX - 16),
          refused: true,
        });
        return;
      }

      const resolution = outcome.resolution;
      resolutionRef.current = resolution;
      /*
        Marking the row is what makes a nest legible: an indented line under a
        parent and a line between two siblings are a few pixels apart, and the
        difference is the whole outcome. It follows the RESOLUTION, not the
        zone — a nest caused by dragging sideways used to highlight nothing.
      */
      setIntoId(outcome.reparenting ? outcome.parentId : null);
      const indent = DEPTH_BASE_PX + resolution.depth * INDENT_PX;
      setIndicator({
        top: zone === 'before' ? rect.top : rect.bottom,
        left: rect.left + indent,
        width: Math.max(80, rect.width - indent - 16),
        refused: false,
      });
    };

    const finish = (commit: boolean) => {
      const resolution = resolutionRef.current;
      if (commit && resolution) {
        dispatch({
          type: 'MOVE_SUBTREE',
          sourceId: draggingId,
          target: { parentId: resolution.parentId, childIndex: resolution.childIndex },
        });
      }
      resolutionRef.current = null;
      originRef.current = null;
      setDraggingId(null);
      setIndicator(null);
      setIntoId(null);
      setOutcome(null);
    };

    const onPointerUp = () => finish(true);
    // A cancelled pointer (the browser taking over, a touch interruption) is not
    // a drop and must never commit.
    const onPointerCancel = () => finish(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
    };

    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [draggingId, dispatch, scrollElement]);

  return { cardRef, draggingId, indicator, intoId, outcome, startDrag };
};
